import { inject, Injectable } from '@angular/core';
import { TranslationObject } from '@ngx-translate/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { FileService } from '../../shared/services/file.service';
import { Questions } from '../models/question.model';

const QUESTIONS_BASE_PATH = 'questions';
const QUESTION_BANK_PATH = `${QUESTIONS_BASE_PATH}/question-bank/questions.json`;
const QUESTION_TRANSLATIONS_PREFIX = `${QUESTIONS_BASE_PATH}/questions-`;
const FALLBACK_LANGUAGE = 'en';

@Injectable({
    providedIn: 'root'
})
export class QuestionsService {
    private readonly fileService = inject(FileService);
    private readonly translationCache: Record<string, TranslationObject> = {};

    /**
     * Loads question bank from CDN only
     * @throws Error if CDN is unavailable
     */
    public getQuestionBank(): Observable<Questions> {
        return this.fileService.getFile<Questions>(QUESTION_BANK_PATH);
    }

    /**
     * Loads question translations with fallback chain:
     * 1. CDN with requested language
     * 2. CDN with fallback language (en)
     * 3. Empty object
     */
    public getQuestionTranslations(languageCode: string): Observable<TranslationObject> {
        return this.fileService.getFile<TranslationObject>(`${QUESTION_TRANSLATIONS_PREFIX}${languageCode}.json`).pipe(
            tap(translations => {
                this.translationCache[languageCode] = translations;
            }),
            catchError(() => (languageCode !== FALLBACK_LANGUAGE ? this.getFallbackTranslations() : of({})))
        );
    }

    /**
     * Resolves question translation from cache
     */
    public resolveQuestionTranslation(languageCode: string, type: string, id: string): string | undefined {
        return (
            this.getCachedQuestionTranslation(languageCode, type, id) ??
            this.getCachedQuestionTranslation(FALLBACK_LANGUAGE, type, id)
        );
    }

    private getFallbackTranslations(): Observable<TranslationObject> {
        return this.fileService
            .getFile<TranslationObject>(`${QUESTION_TRANSLATIONS_PREFIX}${FALLBACK_LANGUAGE}.json`)
            .pipe(
                tap(translations => {
                    this.translationCache[FALLBACK_LANGUAGE] = translations;
                }),
                catchError(() => of({}))
            );
    }

    private getCachedQuestionTranslation(languageCode: string, type: string, id: string): string | undefined {
        const translations = this.translationCache[languageCode]?.['questions'];
        if (!translations || typeof translations !== 'object') {
            return undefined;
        }

        const categoryTranslations = (translations as TranslationObject)[type];
        if (!categoryTranslations || typeof categoryTranslations !== 'object') {
            return undefined;
        }

        const value = (categoryTranslations as TranslationObject)[id];
        return typeof value === 'string' ? value : undefined;
    }
}
